/**
 * AccessDeniedPage — shown to authenticated users who lack the reviewer or admin role.
 *
 * This is a friendly, non-alarming state that explains the situation and gives
 * the user a next step (sign in with a different account, or request access).
 *
 * Rendered by RequireRole as its default fallback; also reachable directly at
 * /access-denied if the router wires that route (F4 owns the routes).
 */

import { makeStyles, mergeClasses, tokens, Text, Title2, Body1 } from '@fluentui/react-components';
import { ShieldErrorRegular } from '@fluentui/react-icons';
import { AppButton } from '@/shared/components/ui';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '@/config/auth';
import { useLayoutStyles } from '@/shared/styles/layoutStyles';

const useStyles = makeStyles({
  root: {
    minHeight: '60vh',
    paddingTop: tokens.spacingVerticalXXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    textAlign: 'center',
  },
  icon: {
    width: '64px',
    height: '64px',
    color: tokens.colorPaletteRedForeground1,
    marginBottom: tokens.spacingVerticalL,
  },
  title: {
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground1,
  },
  body: {
    maxWidth: '480px',
    marginBottom: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
  },
  requestAccessLink: {
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    textDecoration: 'none',
  },
  footer: {
    marginTop: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground4,
  },
});

export function AccessDeniedPage() {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const { instance, accounts } = useMsal();
  const isSignedIn = accounts.length > 0;

  function handleSignInWithDifferentAccount() {
    void instance.loginRedirect({ ...loginRequest, prompt: 'select_account' });
  }

  return (
    <div className={mergeClasses(layout.flexCol, layout.itemsCenter, layout.justifyCenter, styles.root)}>
      <ShieldErrorRegular className={styles.icon} />

      <Title2 as="h1" className={styles.title}>
        Access Restricted
      </Title2>

      <Body1 className={styles.body}>
        {isSignedIn
          ? 'Your account does not have permission to view the reviewer dashboard. If you believe this is an error, please contact the system administrator to request access.'
          : 'You must sign in with a University of Utah Health account that has reviewer access to view this page.'}
      </Body1>

      <div className={mergeClasses(layout.flexCol, layout.itemsCenter, layout.gapS)}>
        {isSignedIn ? (
          <>
            <AppButton variant="secondary" onClick={handleSignInWithDifferentAccount}>
              Sign in with a different account
            </AppButton>
            <a
              href="mailto:its-help@utah.edu?subject=External%20Intake%20Reviewer%20Access%20Request"
              className={styles.requestAccessLink}
            >
              Request access
            </a>
          </>
        ) : (
          <AppButton
            variant="primary"
            onClick={() => void instance.loginRedirect(loginRequest)}
          >
            Sign in
          </AppButton>
        )}
      </div>

      <Text size={200} className={styles.footer}>
        If you need help, contact{' '}
        <a href="mailto:its-help@utah.edu">its-help@utah.edu</a>.
      </Text>
    </div>
  );
}

export default AccessDeniedPage;
